
import { Trek } from '../types/trek-types';
import { ApiFactory } from '@/api/api-factory';
import Analytics from '@/lib/analytics';

/**
 * Search for treks based on a query string
 * This uses the enhanced full-text search and location-based search capabilities
 */
export const searchTreks = async (query: string): Promise<Trek[]> => {
  if (!query || query.trim().length < 2) return [];
  
  try {
    const startTime = performance.now();
    const results = await ApiFactory.getClient().searchTreks(query);
    const endTime = performance.now();
    
    // Track search performance
    Analytics.track('Search Performance', {
      query: query,
      results_count: results.length,
      duration_ms: Math.round(endTime - startTime)
    });
    
    // Also track the search query itself
    Analytics.track('Search Query', {
      query: query,
      results_count: results.length
    });
    
    return results;
  } catch (error) {
    console.error('Error searching treks:', error);
    return [];
  }
};

/**
 * Get common search terms based on previous searches
 * This retrieves popular search terms from the database
 */
export const getCommonSearchTerms = async (): Promise<string[]> => {
  return ApiFactory.getClient().getCommonSearchTerms();
};

/**
 * Get popular search terms that users frequently search for
 * This retrieves terms sorted by popularity from the database
 */
export const getPopularSearchTerms = async (): Promise<string[]> => {
  try {
    // Get the popular terms from the database
    const apiTerms = await ApiFactory.getClient().getCommonSearchTerms();
    if (apiTerms && apiTerms.length > 0) {
      return apiTerms;
    }
    
    // Fallback to default popular terms if API fails or returns empty
    return [
      'Kedarkantha',
      'Valley of Flowers',
      'Hampta Pass',
      'Nandi Hills',
      'Uttari Betta',
      'Kumara Parvata'
    ];
  } catch (error) {
    console.error('Error fetching popular search terms:', error);
    // Fallback to default popular terms if API fails
    return [
      'Kedarkantha',
      'Valley of Flowers',
      'Hampta Pass',
      'Nandi Hills',
      'Uttari Betta'
    ];
  }
};

/**
 * Get search suggestions based on a query
 * This retrieves suggestions from the database
 */
export const getSearchSuggestions = async (query: string): Promise<string[]> => {
  if (!query || query.trim() === '') {
    return [];
  }
  
  try {
    // Add a small delay to simulate network request
    await new Promise(resolve => setTimeout(resolve, 100));
    
    // Try to get suggestions from API
    const apiSuggestions = await ApiFactory.getClient().getSearchSuggestions(query);
    
    if (apiSuggestions && apiSuggestions.length > 0) {
      // Return top suggestions that match the query
      return apiSuggestions
        .filter(suggestion => 
          suggestion.toLowerCase().includes(query.toLowerCase()))
        .slice(0, 6); // Reduce from 10 to 6 to leave room for trek results
    }
    
    // If API returns no results, check if it's a location-based query
    const nearMatch = query.toLowerCase().match(/\b(near|around|close to|nearby)\s+([a-z\s]+)/i);
    if (nearMatch && nearMatch[2]) {
      const locationName = nearMatch[2].trim();
      
      // Add location-based suggestions
      return [
        `Treks near ${locationName}`,
        `Hiking trails around ${locationName}`,
        `${locationName} weekend treks`,
        `${locationName} hiking spots`
      ];
    }
    
    // If still no results, fallback to local suggestions
    const localSuggestions = [
      'Himalayan Treks',
      'Karnataka Treks',
      'Uttarakhand Treks',
      'Winter Treks',
      'Weekend Treks',
      'Beginner Friendly Treks',
      'Nandi Hills',
      'Valley of Flowers',
      'Kedarkantha Trek',
      'Hampta Pass Trek',
      'Treks near Bangalore',
      'Family Treks',
      'Monsoon Treks',
      'Alpine Meadows'
    ];
    
    return localSuggestions
      .filter(suggestion => 
        suggestion.toLowerCase().includes(query.toLowerCase()))
      .slice(0, 6); // Reduce from 8 to 6 to leave room for trek results
  } catch (error) {
    console.error('Error getting search suggestions:', error);
    
    // Return empty array in case of error
    return [];
  }
};

/**
 * Get both search suggestions and top trek results simultaneously
 * This provides a comprehensive search experience
 */
export const getSearchResults = async (query: string): Promise<{
  suggestions: string[];
  treks: Trek[];
}> => {
  if (!query || query.trim().length < 2) {
    return { suggestions: [], treks: [] };
  }
  
  try {
    // Run both queries in parallel for better performance
    const [suggestions, treks] = await Promise.all([
      getSearchSuggestions(query),
      searchTreks(query)
    ]);
    
    return {
      suggestions: suggestions.slice(0, 5), // Limit to 5 suggestions
      treks: treks.slice(0, 3) // Show top 3 trek results
    };
  } catch (error) {
    console.error('Error fetching search results:', error);
    return { suggestions: [], treks: [] };
  }
};

/**
 * Record when a user clicks on a search suggestion
 */
export const recordSuggestionClick = async (suggestion: string): Promise<void> => {
  try {
    await ApiFactory.getClient().recordSuggestionClick(suggestion);
  } catch (error) {
    console.error('Error recording suggestion click:', error);
  }
};
